import type { Brand } from "@staffsmith/api-types";
import { importPage, PRODUCT_NAME } from "@staffsmith/web-auth";
import { applyBrand } from "@staffsmith/theme";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  type RouteObject,
  useLocation,
  useNavigate,
} from "react-router";
import { ApiError } from "./api.js";
import { useServices } from "./context.js";
import { SignInPage } from "./pages/SignIn.js";
import { ErrorNote, Loading } from "./ui.js";

// One portal for both kinds of portal user: a consultant lands on their time, a
// client contact on their shortlist (and their company's timesheets). Each page
// loads only when first visited (ADR 0003: route-level code splitting).
const page = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await importPage(load))?.default ?? (() => null),
});

// The staffing firm's own mark: its square mark beside its name (beside,
// never instead of, the Demo tag); the full logo only when no name is shown.
// Both are drawn only as images, so nothing in them runs.
const svgSrc = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
function FirmMark({ brand, name }: { brand: Brand; name: string }) {
  const shown = brand.short_name ?? name;
  if (!shown)
    return (
      <span className="firm-mark">
        {brand.logo_svg ? <img src={svgSrc(brand.logo_svg)} alt="" height={30} /> : null}
      </span>
    );
  return (
    <span className="firm-mark">
      {brand.mark_svg ? (
        <img className="firm-square" src={svgSrc(brand.mark_svg)} alt="" width={30} height={30} />
      ) : null}
      <span className="firm-name">{shown}</span>
    </span>
  );
}

// The firm's colors as tokens (design v0.9, V1), once the sign-in brings them.
function BrandTokens({ brand }: { brand: Brand | null | undefined }) {
  const primary = brand?.primary_color;
  const accent = brand?.accent_color;
  useEffect(() => {
    applyBrand({ primary_color: primary ?? null, accent_color: accent ?? null });
  }, [primary, accent]);
  return null;
}

function Layout() {
  const { api, auth } = useServices();
  const navigate = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: auth.isSignedIn() });
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Choosing a page closes the menu.
  useEffect(() => setMenuOpen(false), [pathname]);
  if (!auth.isSignedIn()) return <Navigate to="/sign-in" replace />;
  const noAccess = me.error instanceof ApiError && me.error.status === 403;
  const brand = me.data?.brand;
  return (
    <div className="shell">
      {/* The firm's band across the top (design v0.9, V6): its mark and name,
          a menu button on a phone, and a greeting. */}
      <header className="portal-header weave accent-edge-bottom">
        <BrandTokens brand={brand} />
        <div className="portal-top">
          {brand ? (
            <FirmMark brand={brand} name={me.data?.firm_name ?? ""} />
          ) : (
            <span className="firm-mark">
              <span className="firm-name">{PRODUCT_NAME}</span>
            </span>
          )}
          {me.data?.demo ? (
            <span className="badge demo" title="Synthetic data only">
              Demo
            </span>
          ) : null}
          <span className="spacer" />
          <button
            type="button"
            className="menu-button"
            aria-expanded={menuOpen}
            aria-controls="portal-nav"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            Menu
          </button>
        </div>
        <nav
          id="portal-nav"
          className={menuOpen ? "portal-nav open" : "portal-nav"}
          aria-label="Main"
        >
          {me.data?.kind === "client_contact" ? (
            <>
              <NavLink to="/shortlist">Shortlist</NavLink>
              <NavLink to="/approvals">Timesheets</NavLink>
            </>
          ) : null}
          {me.data?.kind === "consultant" ? (
            <>
              <NavLink to="/getting-started">Getting started</NavLink>
              <NavLink to="/time">Your time</NavLink>
              <NavLink to="/pay">Your pay</NavLink>
              <NavLink to="/skills">Your skills</NavLink>
              <NavLink to="/privacy">Your privacy</NavLink>
            </>
          ) : null}
          <span className="spacer" />
          <button
            type="button"
            className="link"
            onClick={() => {
              auth.signOut();
              void navigate("/sign-in");
            }}
          >
            Sign out
          </button>
        </nav>
        {me.data?.name ? (
          <p className="greeting">
            Hello, {me.data.name.split(" ")[0]} · <span className="brand">{PRODUCT_NAME}</span>
          </p>
        ) : null}
      </header>
      <main>
        {me.isPending ? <Loading /> : null}
        {noAccess ? (
          <p className="notice">
            This sign-in has no portal access. Ask your {PRODUCT_NAME} contact.
          </p>
        ) : (
          <ErrorNote error={me.error} />
        )}
        {me.data ? <Outlet /> : null}
      </main>
    </div>
  );
}

function Home() {
  const { api } = useServices();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  if (!me.data) return null;
  return <Navigate to={me.data.kind === "consultant" ? "/time" : "/shortlist"} replace />;
}

export const routes: RouteObject[] = [
  { path: "/sign-in", element: <SignInPage /> },
  // The booking page needs no sign-in: its signed link is the key (C13d).
  { path: "/book/:token", lazy: page(() => import("./pages/Book.js")) },
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Home /> },
      { path: "time", lazy: page(() => import("./pages/Time.js")) },
      { path: "getting-started", lazy: page(() => import("./pages/Onboarding.js")) },
      { path: "pay", lazy: page(() => import("./pages/Pay.js")) },
      { path: "privacy", lazy: page(() => import("./pages/Privacy.js")) },
      { path: "skills", lazy: page(() => import("./pages/Skills.js")) },
      { path: "shortlist", lazy: page(() => import("./pages/Shortlist.js")) },
      { path: "approvals", lazy: page(() => import("./pages/Approvals.js")) },
    ],
  },
];
